// frontend/src/App.jsx

import React, { useState, useEffect, useRef } from 'react';
import './index.css';
import axios from 'axios';
import { Send, Trash2, Download, BookOpen, XCircle, RefreshCw, Copy } from 'lucide-react';
import DocsPage from './components/DocsPage';
import TranscriptCard from './components/TranscriptCard';
import SubtitleEditor, { buildSrt, buildTxt } from './components/SubtitleEditor';
import ErrorBoundary from './components/ErrorBoundary';
import { clientLog, clientError, setLogUploadId, getSessionId, flushClientLog } from './clientLog';
import {
  uploadFileInChunks, loadPendingUpload, clearPendingUpload, pendingMatchesFile,
} from './chunkedUpload';

// ── 選項常數（UI 用）────────────────────────────
const MODEL_OPTIONS = [
  { value: 'breeze',    label: 'Breeze ASR 25', tag: '繁中', hint: 'MediaTek 開發，繁體中文與中英混用最佳化。日常中文轉錄的首選。' },
  { value: 'taiwanese', label: 'Breeze ASR 26', tag: '台語', hint: 'MediaTek Research 開發，台灣閩南語專用，輸出為中文漢字。選此模型會自動切換語言為台語。' },
  { value: 'turbo',     label: 'large-v3-turbo', tag: null,  hint: 'OpenAI 2024 年推出，速度最快、品質佳，適合長音檔。' },
  { value: 'large-v3',  label: 'large-v3',      tag: null,  hint: 'OpenAI 最高品質模型，速度較慢但準確度最佳。' },
  { value: 'medium',    label: 'medium',        tag: null,  hint: 'OpenAI 中型通用模型，速度與品質平衡。' },
];

// 自動偵測只給 Whisper 系列用：Breeze ASR 25／26 是針對中文與台語微調過的，
// 讓它們自己判斷語言只會得到更差的結果，所以選 Breeze 時這個選項會消失。
const AUTO_LANG = 'auto';
const AUTO_LANG_MODELS = ['turbo', 'large-v3', 'medium'];
const AUTO_LANG_OPTION = { value: AUTO_LANG, label: '自動偵測' };

const LANG_OPTIONS = [
  { value: 'zh',  label: '中文' },
  { value: 'nan', label: '台語' },   // 綁定 Breeze ASR 26（唯一支援台語的模型）
  { value: 'en',  label: 'English' },
  { value: 'ja',  label: '日本語' },
  { value: 'es',  label: 'Español' },
];

// ── 媒體檔判斷 ──────────────────────────────────
// 拖放路徑以前只看 file.type.startsWith('audio/')，但 Windows 上的 .opus、
// .amr、部分 .m4a 的 MIME 是空字串或 application/octet-stream，會被當成
// 「不是音訊」直接拒收；點選路徑則完全不檢查，同一個檔案在兩條路上的結果
// 竟然不一樣。統一成這一個函式：MIME 認得就過，認不得才看副檔名。
const MEDIA_EXTENSIONS = [
  'wav', 'mp3', 'm4a', 'aac', 'flac', 'ogg', 'opus', 'amr', 'wma',
  'mp4', 'mov', 'mkv', 'webm', 'avi', 'm4v', '3gp',
];
const MEDIA_ACCEPT = `audio/*,video/*,${MEDIA_EXTENSIONS.map(e => `.${e}`).join(',')}`;
const NOT_MEDIA_MSG = '請選擇音訊或影片檔（WAV、MP3、M4A、AAC、FLAC、OGG、OPUS、MP4、MOV、MKV 等）';

function isMediaFile(file) {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  if (type.startsWith('audio/') || type.startsWith('video/')) return true;
  // 有明確 MIME 但不是音訊／影片 → 真的不是媒體檔
  if (type && type !== 'application/octet-stream') return false;
  // MIME 為空或 octet-stream：只能看副檔名
  const name = (file.name || '').toLowerCase();
  const dot = name.lastIndexOf('.');
  return dot >= 0 && MEDIA_EXTENSIONS.includes(name.slice(dot + 1));
}

// 影片副檔名：MIME 讀不到時用來決定要拿 <video> 還是 <audio> 讀 metadata
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'mkv', 'webm', 'avi', 'm4v', '3gp'];
// 超過這個大小的影片才值得提醒「先抽音軌」：再小的話省下來的時間有限，
// 多一段提示只是雜訊
const LARGE_VIDEO_BYTES = 200 * 1024 * 1024;

function isVideoFile(file) {
  if (!file) return false;
  if ((file.type || '').toLowerCase().startsWith('video/')) return true;
  const name = (file.name || '').toLowerCase();
  const dot = name.lastIndexOf('.');
  return dot >= 0 && VIDEO_EXTENSIONS.includes(name.slice(dot + 1));
}

// 讀出音檔／影片的長度。瀏覽器讀得到 metadata 就有值，讀不到就回 null
// （AMR、某些 MKV 容器在部分瀏覽器上就是讀不出來）——這時只顯示檔案大小，
// 不要因為算不出預估時間就整段資訊都不給。
function probeDuration(file) {
  return new Promise((resolve) => {
    let objectUrl = null;
    let settled = false;
    const el = document.createElement(isVideoFile(file) ? 'video' : 'audio');

    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        el.removeAttribute('src');
        el.load();
      } catch (e) { /* 已經被瀏覽器收掉 */ }
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      resolve(value);
    };

    // 有些格式會卡在 loading 不噴 error，不設上限會永遠等下去
    const timer = setTimeout(() => finish(null), 8000);

    try {
      el.preload = 'metadata';
      el.onloadedmetadata = () => finish(
        Number.isFinite(el.duration) && el.duration > 0 ? el.duration : null
      );
      el.onerror = () => finish(null);
      objectUrl = URL.createObjectURL(file);
      el.src = objectUrl;
    } catch (e) {
      finish(null);
    }
  });
}

// 估時係數。後端 /server-status 的 rtf 欄位是各模型目前生效的實測值，
// 拿不到就用與後端 jobs.py 相同的預設值，兩邊才不會給出不一樣的數字。
const DEFAULT_RTF = 0.3;
const DIARIZATION_FACTOR = 0.01;   // 與 backend/jobs.py 的 DIARIZATION_FACTOR 一致

// 台語與 ASR 26 互相綁定：只有這個模型聽得懂台語
const TAIGI_LANG = 'nan';
const TAIGI_MODEL = 'taiwanese';

// 「工作進行中」的 wsStatus：喚醒鎖與「取消」按鈕都用這一組判斷
const PROCESSING_STATUSES = [
  'connecting', 'queued', 'downloading', 'optimizing', 'waiting_gpu', 'streaming', 'diarizing', 'reconnecting',
];

const TASK_OPTIONS = [
  { value: 'transcribe', label: '轉錄' },
  { value: 'translate',  label: '翻譯為英文' },
];

// 簡易的 Cookie 操作
function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 864e5);
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/`;
}
function getCookie(name) {
  return document.cookie.split('; ').reduce((r, v) => {
    const [k, ...vs] = v.split('=');
    return k === name ? decodeURIComponent(vs.join('=')) : r;
  }, '');
}
function deleteCookie(name) {
  setCookie(name, '', -1);
}

function humanSize(bytes) {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** i).toFixed(i ? 1 : 0)} ${units[i]}`;
}

// ── 手機／App 內建瀏覽器相關 ─────────────────────
// 偵測 LINE、Facebook、Instagram 等 App 內建瀏覽器（WebView）。
// 這類環境常拿不到 <input type="file"> 選到的檔案內容（尤其相簿或雲端來源），
// XHR 會在還沒送出任何位元組時就失敗，畫面上只看得到 axios 的 "Network Error"，
// 伺服器端連一筆紀錄都不會有。
function detectInAppBrowser() {
  const ua = navigator.userAgent || '';
  if (/\bLine\//i.test(ua)) return 'LINE';
  if (/FBAN|FBAV|FB_IAB/i.test(ua)) return 'Facebook';
  if (/Instagram/i.test(ua)) return 'Instagram';
  if (/MicroMessenger/i.test(ua)) return 'WeChat';
  return null;
}

// 上傳前先試讀檔案開頭 64KB。讀不到就直接給明確訊息，
// 不要等到 XHR 用 "Network Error" 這種看不出原因的字樣失敗。
async function ensureFileReadable(file) {
  const head = file.slice(0, 64 * 1024);
  try {
    if (typeof head.arrayBuffer === 'function') {
      await head.arrayBuffer();
    } else {
      await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = resolve;
        r.onerror = () => reject(r.error || new Error('FileReader error'));
        r.readAsArrayBuffer(head);
      });
    }
  } catch (e) {
    const inApp = detectInAppBrowser();
    throw new Error(
      `瀏覽器無法讀取「${file.name}」的內容（${e && e.name ? e.name : '讀取失敗'}）。` +
      (inApp
        ? `你目前在 ${inApp} 的內建瀏覽器中，這是常見原因，請改用 Chrome 或 Safari 開啟本頁。`
        : '若檔案來自雲端相簿（如 Google 相簿），請先把檔案下載到手機再上傳。')
    );
  }
}

// 把 axios 的錯誤翻成看得懂的訊息，並附上判斷線索（送了多少、什麼瀏覽器）。
function describeUploadError(err, sentBytes, file) {
  const data = err.response?.data;
  // FastAPI 的錯誤是 {"detail": "..."}，物件直接丟給 React 當子元素會整頁炸掉
  if (data) {
    if (typeof data === 'string') return data;
    if (data.detail) return typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
  }
  const status = err.response?.status;
  if (status === 413) return '檔案超過伺服器上限（10GB），請壓縮或分段後再上傳。';
  if (status) return `上傳失敗：伺服器回應 HTTP ${status}`;

  const inApp = detectInAppBrowser();
  const sizeText = file ? `，檔案 ${humanSize(file.size)}` : '';
  if (err.code === 'ERR_NETWORK' || /Network Error/i.test(err.message || '')) {
    if (!sentBytes) {
      return '上傳失敗：瀏覽器還沒送出任何資料就中斷了' + sizeText + '。' +
        (inApp
          ? ` 你正在使用 ${inApp} 的內建瀏覽器，請改用 Chrome／Safari 開啟本頁再試。`
          : ' 常見原因：檔案來自雲端相簿尚未下載到手機、或瀏覽器沒有檔案讀取權限。');
    }
    return `上傳失敗：連線在傳了 ${humanSize(sentBytes)}${sizeText} 後中斷。` +
      ' 手機上傳期間請保持螢幕開啟、不要切換 App；改用 Wi-Fi 會穩定很多。';
  }
  if (err.code === 'ECONNABORTED') return '上傳逾時，請檢查網路後再試一次。';
  return `上傳失敗：${err.message || '未知錯誤'}`;
}

// navigator.clipboard 只在 HTTPS（或 localhost）可用，舊版 Safari／WebView 也沒有。
// 退回隱藏 textarea + execCommand：這條路很老但幾乎到處都能用。
function legacyCopy(text) {
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, ta.value.length);   // iOS Safari 少了這行會複製到空字串
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
}

// ── 結果暫存（localStorage）──────────────────────
// 轉錄完成後把結果留在瀏覽器裡。以前 done 一到就把 cookie 刪掉、結果只活在
// React state，使用者手滑重整或關掉分頁就什麼都沒了，只能整份重跑。
const LAST_RESULT_KEY = 'whisper_last_result';
const LAST_RESULT_MAX_BYTES = 4 * 1024 * 1024;

function saveLastResult(record) {
  try {
    const json = JSON.stringify(record);
    // localStorage 以 UTF-16 計算，一個字元約 2 bytes；配額通常 5MB，留餘裕給診斷碼等其他 key
    const approxBytes = json.length * 2;
    if (approxBytes > LAST_RESULT_MAX_BYTES) {
      clientLog('result_cache_skip', { bytes: approxBytes, segments: (record.segments || []).length });
      return false;
    }
    localStorage.setItem(LAST_RESULT_KEY, json);
    clientLog('result_cached', { bytes: approxBytes, segments: (record.segments || []).length });
    return true;
  } catch (e) {
    // 無痕模式或配額已滿：存不下就算了，畫面上的結果照顯示
    clientLog('result_cache_error', { message: String((e && e.message) || e) });
    return false;
  }
}

function loadLastResult() {
  try {
    const raw = localStorage.getItem(LAST_RESULT_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data && typeof data.text === 'string' ? data : null;
  } catch (e) {
    return null;
  }
}

function clearLastResult() {
  try {
    localStorage.removeItem(LAST_RESULT_KEY);
  } catch (e) {
    // 忽略：清不掉也不影響
  }
}

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '' : d.toLocaleString();
  } catch (e) {
    return '';
  }
}

// ── 完成通知 ────────────────────────────────────
// 長音檔動輒十幾分鐘，使用者一定會把分頁丟到背景做別的事，回來才發現早就跑完。
// 分頁不在前景時同時用三種方式提醒：系統通知、分頁標題閃爍、一聲提示音。

let titleFlashTimer = null;
let titleBeforeFlash = '';

function startTitleFlash(text) {
  if (titleFlashTimer) return;         // 已經在閃了就不要疊第二個計時器
  titleBeforeFlash = document.title;
  let on = true;
  document.title = text;
  titleFlashTimer = setInterval(() => {
    on = !on;
    document.title = on ? text : titleBeforeFlash;
  }, 1000);
}

function stopTitleFlash() {
  if (!titleFlashTimer) return;
  clearInterval(titleFlashTimer);
  titleFlashTimer = null;
  document.title = titleBeforeFlash;
}

// 回到前景就還原標題（使用者已經看到了，不需要再閃）
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') stopTitleFlash();
});

// 用 WebAudio 合成一聲短提示音，不引入音檔（省一個網路請求與一份靜態資源）
function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.value = 880;
    // 直接給定值會有爆音，用指數包絡淡入淡出
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
    osc.start();
    osc.stop(ctx.currentTime + 0.36);
    osc.onended = () => { try { ctx.close(); } catch (e) { /* 已關閉 */ } };
  } catch (e) {
    // 自動播放政策擋掉、或瀏覽器沒有 WebAudio：沒聲音不影響主流程
  }
}

// 只在分頁不在前景時提醒；在前景的話畫面本來就看得到，不必打擾
function notifyIfHidden({ title, body, flash }) {
  if (document.visibilityState === 'visible') return false;
  startTitleFlash(flash);
  beep();
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      const n = new Notification(title, { body, tag: 'whisper-job' });
      n.onclick = () => { window.focus(); stopTitleFlash(); n.close(); };
    }
  } catch (e) {
    // Android Chrome 只允許從 Service Worker 發通知，會丟 TypeError；標題與音效仍有效
  }
  return true;
}

// 權限只在使用者手勢內問一次：permission 已經是 granted/denied 就不要再打擾
function requestNotifyPermission() {
  try {
    if (typeof Notification === 'undefined') {
      clientLog('notify_perm', { supported: false });
      return;
    }
    if (Notification.permission !== 'default') {
      clientLog('notify_perm', { asked: false, result: Notification.permission });
      return;
    }
    // 新版瀏覽器回 Promise，舊版 Safari 只吃 callback；兩種都接，用旗標避免記兩筆
    let logged = false;
    const record = (r) => {
      if (logged) return;
      logged = true;
      clientLog('notify_perm', { asked: true, result: r });
    };
    const res = Notification.requestPermission(record);
    if (res && typeof res.then === 'function') res.then(record).catch(() => {});
  } catch (e) {
    clientLog('notify_perm', { asked: true, error: String(e && e.message) });
  }
}

// WebSocket 建構子的相對路徑（"/ws/..."）要到 Chrome 125 / Safari 17 才支援，
// 舊版 Android 瀏覽器與部分 WebView 會直接丟 SyntaxError。一律組成絕對網址。
function buildWsUrl(apiBase, path) {
  if (apiBase) return apiBase.replace(/^http/, 'ws') + path;
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}${path}`;
}

export default function App() {
  // ── 參數設定 ─────────────────────────
  const [selectedModel, setSelectedModel]   = useState('breeze'); // 預設使用 Breeze ASR 25
  const [selectedLang, setSelectedLang]     = useState('zh');
  const [selectedTask, setSelectedTask]     = useState('transcribe');
  const [useAudioOptimization, setUseAudioOptimization] = useState(true);
  const [useDiarization, setUseDiarization] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [activeTab, setActiveTab] = useState('transcribe');

  // ── 檔案 & URL ─────────────────────────
  const [uploadedFile, setUploadedFile]     = useState(null);
  const [url, setUrl]                       = useState('');
  const [fileInputKey, setFileInputKey]     = useState(Date.now());

  // ── 進度 & 結果 ─────────────────────────
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSpeed, setUploadSpeed]       = useState(0);
  const [uploadDetail, setUploadDetail]     = useState({ loaded: 0, total: 0 });
  const [uploadId, setUploadId]             = useState(null);
  const [wsStatus, setWsStatus]             = useState('idle'); // idle|connecting|queued|downloading|optimizing|streaming|diarizing|done
  // 排隊資訊：{ position, ahead, waiting, running }，非排隊中為 null
  const [queueInfo, setQueueInfo]           = useState(null);
  const [gpuWaitMessage, setGpuWaitMessage] = useState('');
  // 伺服器整體佇列（上傳前就能看到目前多忙）
  const [serverQueue, setServerQueue]       = useState(null);
  const [progress, setProgress]             = useState(0);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadSpeed, setDownloadSpeed]   = useState(0);
  const [downloadDetail, setDownloadDetail] = useState({ downloaded: 0, total: 0 });
  const [remaining, setRemaining]           = useState(0);
  // 超時秒數：ETA 倒數歸零後還沒收到結果時，改成累加已經超過多久（0 = 還在預估內）
  const [overtime, setOvertime]             = useState(0);
  const [segments, setSegments]             = useState([]);
  const [editedSegments, setEditedSegments] = useState([]);  // 字幕編輯器的即時內容
  const [fullText, setFullText]             = useState('');
  // 只有真的收到 done（含還原的結果）才可能顯示「沒有辨識到語音內容」；
  // 任務失敗時 wsStatus 也是 done，不分開記的話錯誤面板下面會多一塊誤導人的空結果面板
  const [resultReceived, setResultReceived] = useState(false);
  const [errorMsg, setErrorMsg]             = useState('');
  // 這則錯誤是不是「重試就有機會成功」的上傳失敗。只有這種才給重試按鈕：
  // 轉錄失敗、檔案格式不對按幾次都是同樣的結果，給了只會讓人白試。
  const [uploadRetryable, setUploadRetryable] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [serverStatus, setServerStatus] = useState(null);
  // 防休眠狀態：null = 沒在忙（不顯示）、true = 已取得喚醒鎖、false = 不支援或被拒
  const [wakeLockOn, setWakeLockOn]         = useState(null);
  // 從 localStorage／伺服器還原回來的舊結果（null = 這一輪剛跑完的）
  const [restoredResult, setRestoredResult] = useState(null);

  // 選到的檔案剛好有一份沒傳完的紀錄時，先在畫面上講明會續傳
  const [resumeHint, setResumeHint]         = useState(false);
  // 選到的檔案音長（秒）。null = 還沒讀到或讀不出來，只顯示大小
  const [fileDuration, setFileDuration]     = useState(null);

  const wsRef           = useRef(null);
  const activeUploadIdRef = useRef(uploadId);
  activeUploadIdRef.current = uploadId;
  const timerRef        = useRef(null);
  const uploadStartRef  = useRef(0);
  const uploadSentRef   = useRef(0);   // 失敗當下已送出的位元組數：分辨「沒送出」與「中途斷線」
  // 分段上傳會無限重試到成功為止，唯一的出口是使用者按重置
  const uploadAbortRef  = useRef({ canceled: false });
  // 通知內容要帶檔名，但 WebSocket 的 effect 只在 uploadId 變動時重建，
  // 直接讀 state 會拿到舊值，改用 ref 存最新的工作名稱
  const jobNameRef      = useRef('');
  const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

  // ── App 內建瀏覽器處理 ─────────────────────
  const [inAppBrowser] = useState(() => detectInAppBrowser());
  // LINE：網址加上 openExternalBrowser=1 會改用系統瀏覽器開啟（LINE 官方支援的參數），
  // 一勞永逸避開 WebView 讀不到檔案的問題。其他 App 只能靠畫面提示請使用者手動切換。
  useEffect(() => {
    clientLog('page_load', { inApp: inAppBrowser });
  }, [inAppBrowser]);
  useEffect(() => {
    if (inAppBrowser !== 'LINE') return;
    const u = new URL(window.location.href);
    if (u.searchParams.get('openExternalBrowser') === '1') return;
    u.searchParams.set('openExternalBrowser', '1');
    clientLog('line_redirect');
    flushClientLog();
    window.location.replace(u.toString());
  }, [inAppBrowser]);
  const showDocs = window.__RUNTIME_CONFIG__?.SHOW_DOCS ?? true;

  // ── 伺服器狀態輪詢 ─────────────────────────
  useEffect(() => {
    const fetchStatus = () => {
      fetch(`${API_BASE}/server-status`)
        .then(res => res.json())
        .then(data => {
          setServerStatus(data);
          // 佇列資訊：讓使用者上傳前就知道現在多忙
          if (data && data.queue_limit != null) {
            setServerQueue({
              running: data.queue_running || 0,
              waiting: data.queue_waiting || 0,
              limit: data.queue_limit,
            });
          }
        })
        .catch(() => { setServerStatus(null); setServerQueue(null); });
    };
    fetchStatus();
    // 排隊中縮短輪詢間隔，讓「前面還有幾位」更即時
    const id = setInterval(fetchStatus, wsStatus === 'queued' ? 3000 : 10000);
    return () => clearInterval(id);
  }, [API_BASE, wsStatus]);

  // ETA 倒數。轉錄中重連或重整頁面時 elapsed 是後端回報的已跑秒數，
  // 倒數要從那裡接續，不能每次都從頭數。
  // 進度是照 ETA 假算的，估少了就會停在 99% / 00:00 不動——那看起來就是當掉。
  // 歸零後改成往上累加「已經超過多久」，並讓進度條變成不定長度的動畫。
  // 之後若又收到新的 estimated_time（例如重連時後端附上 elapsed），整組重設。
  const startCountdown = (estimatedTime, elapsedSeconds) => {
    if (estimatedTime == null) return;
    const eta = Math.max(1, Math.ceil(estimatedTime));
    let elapsed = Math.floor(elapsedSeconds || 0);
    const tick = () => {
      setRemaining(Math.max(0, eta - elapsed));
      setProgress(Math.min(99, Math.floor((elapsed / eta) * 100)));
      setOvertime(Math.max(0, elapsed - eta));
    };
    tick();
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      elapsed++;
      tick();
    }, 1000);
  };

  // 把 /status/ 回報的階段還原成對應畫面。
  // 以前不管伺服器正在做什麼，重整後一律只顯示「正在連線伺服器…」，
  // 使用者完全看不出來自己的檔案其實正在下載或已經轉到一半。
  const applyStatusPhase = (data) => {
    const phase = data && data.phase;
    const prog = (data && data.progress) || null;
    clientLog('status_restored', { phase, eta: data && data.estimated_time, elapsed: data && data.elapsed });
    if (phase === 'queued') {
      setWsStatus('queued');
      setQueueInfo({
        position: (prog && prog.position) || 0,
        ahead: prog && prog.ahead != null ? prog.ahead : Math.max(0, ((prog && prog.position) || 1) - 1),
        waiting: (prog && prog.waiting) || 0,
        running: (prog && prog.running) || 0,
        etaSeconds: prog && prog.eta_seconds != null ? prog.eta_seconds : null,
      });
      return;
    }
    if (phase === 'downloading') {
      setWsStatus('downloading');
      if (prog && prog.download_progress != null) {
        setDownloadProgress(Math.round(prog.download_progress));
        setDownloadSpeed(prog.download_speed || 0);
        setDownloadDetail({
          downloaded: prog.download_downloaded || 0,
          total: prog.download_total || 0,
        });
      }
      return;
    }
    if (phase === 'optimizing') { setWsStatus('optimizing'); return; }
    if (phase === 'waiting_gpu') {
      clearInterval(timerRef.current);
      setQueueInfo(null);
      setGpuWaitMessage((prog && prog.message) || '正在等待 GPU 顯存，請稍候…');
      setWsStatus('waiting_gpu');
      return;
    }
    if (phase === 'diarizing') { setWsStatus('diarizing'); return; }
    if (phase === 'transcribing') {
      setWsStatus('streaming');
      startCountdown(data.estimated_time, data.elapsed);
      return;
    }
    setWsStatus('connecting');
  };

  // 把一份結果直接攤到結果頁（不設 uploadId，所以不會去建 WebSocket）
  const applyResult = (rec, from) => {
    clientLog('result_restored', { from, upload_id: rec.upload_id, segments: (rec.segments || []).length });
    setFullText(rec.text || '');
    setResultReceived(true);
    setSegments(rec.segments || []);
    setEditedSegments([]);
    setProgress(100);
    setRemaining(0);
    setOvertime(0);
    setQueueInfo(null);
    setWsStatus('done');
    setRestoredResult({
      upload_id: rec.upload_id || '',
      name: rec.name || '',
      created: rec.created || '',
      model: rec.model || '',
    });
  };

  // ── 首次載入：接回進行中的任務，或還原上一次的結果 ─────────────────
  useEffect(() => {
    const saved = getCookie('upload_id');
    const cached = loadLastResult();
    if (!saved) {
      // 沒有 cookie（過期或換裝置）→ 至少把本機留的結果放回畫面
      if (cached) applyResult(cached, 'localStorage');
      return;
    }
    axios.get(`${API_BASE}/status/${saved}`)
      .then(res => {
        // 伺服器還認得這個 upload_id：走原本的 WebSocket 流程，
        // 進行中會接回進度，已完成會直接吐快取結果
        if (res.data.active) {
          setUploadId(saved);
          applyStatusPhase(res.data);
          return;
        }
        deleteCookie('upload_id');
        if (cached) {
          applyResult(cached, 'localStorage');
          return;
        }
        // localStorage 被清掉（無痕、換瀏覽器）但 cookie 還在 → 跟伺服器要結果檔
        axios.get(`${API_BASE}/result/${saved}`)
          .then(r => { if (r.data && r.data.done) applyResult({ ...r.data, upload_id: saved }, 'server'); })
          .catch(() => {});
      })
      .catch(() => {
        // 後端連不上：本機還有結果就先顯示，不要讓使用者以為東西不見了
        if (cached) applyResult(cached, 'localStorage');
      });
    // 僅在掛載時檢查一次；API_BASE 為模組常數，不需列入依賴
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── 檔案選擇 ─────────────────────────────
  // 選到的檔案若對得上本機那份未完成的上傳紀錄，先在畫面講明會續傳，
  // 使用者才不會以為系統把他上次傳的十幾分鐘丟掉了
  const noteResumable = (f) => {
    const pending = loadPendingUpload();
    setResumeHint(pendingMatchesFile(pending, f));
  };

  // 拖放與點選共用同一條路：同樣的檢查、同樣的提示、同樣的診斷日誌
  const acceptFile = (f, via) => {
    if (!isMediaFile(f)) {
      clientLog('file_rejected', { name: f.name, type: f.type || '', via });
      setErrorMsg(NOT_MEDIA_MSG);
      setTimeout(() => setErrorMsg(''), 5000);
      return;
    }
    clientLog('file_selected', { name: f.name, size: f.size, type: f.type || '', via });
    setErrorMsg('');
    setUploadedFile(f);
    setFileDuration(null);
    noteResumable(f);
    setUrl('');
    probeDuration(f).then((sec) => {
      setFileDuration(sec);
      clientLog('file_probed', { duration: sec == null ? null : Math.round(sec), ok: sec != null });
    });
  };

  const onFileChange = e => {
    if (e.target.files.length) acceptFile(e.target.files[0], 'picker');
  };

  const openFilePicker = () => {
    const input = document.getElementById('file-input');
    if (input) input.click();
  };

  // ── 拖放功能 ─────────────────────────────
  const [isDragging, setIsDragging] = useState(false);
  // 只需要 setter：用來追蹤巢狀 dragenter/dragleave 的層數
  // eslint-disable-next-line no-unused-vars
  const [dragCounter, setDragCounter] = useState(0);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => {
      const newCount = prev - 1;
      if (newCount === 0) {
        setIsDragging(false);
      }
      return newCount;
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragCounter(0);

    const files = e.dataTransfer.files;
    if (files.length > 0) acceptFile(files[0], 'drop');
  };

  // ── 格式化工具 ─────────────────────────
  const formatSize = (bytes) => {
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
    if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${bytes} B`;
  };
  const formatSpeed = (bytesPerSec) => {
    if (bytesPerSec >= 1024 * 1024) return `${(bytesPerSec / 1024 / 1024).toFixed(1)} MB/s`;
    if (bytesPerSec >= 1024) return `${(bytesPerSec / 1024).toFixed(0)} KB/s`;
    return `${bytesPerSec.toFixed(0)} B/s`;
  };
  // 把秒數轉成 mm:ss（超過一小時用 h:mm:ss）。轉錄倒數與排隊等待共用同一套顯示：
  // 以前倒數直接印秒數，長音檔會出現「預估剩餘 3782 秒」這種沒人看得懂的字。
  const formatDuration = (sec) => {
    const total = Math.max(0, Math.ceil(sec || 0));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
  };

  // ── 上傳（含進度）─────────────────────────
  const handleUpload = async () => {
    if (uploadAbortRef.current.busy) return;
    setErrorMsg('');
    setUploadRetryable(false);
    if (!uploadedFile && !url) {
      setErrorMsg('請先選擇檔案或輸入 URL');
      return;
    }
    const abort = { canceled: false, busy: true };
    uploadAbortRef.current = abort;
    setSubmitting(true);
    clientLog('upload_start', uploadedFile
      ? { name: uploadedFile.name, size: uploadedFile.size, type: uploadedFile.type || '' }
      : { url });
    jobNameRef.current = uploadedFile ? uploadedFile.name : url;
    // 通知權限一定要在使用者手勢裡問，事後（收到 done 時）才問瀏覽器會直接拒絕
    requestNotifyPermission();

    try {
      setUploadProgress(0);
      setUploadSpeed(0);
      setUploadDetail({ loaded: 0, total: 0 });
      uploadStartRef.current = Date.now();
      uploadSentRef.current = 0;

      // 上傳進度共用的更新方式：速度照舊用「已確認位元組 ÷ 經過秒數」算
      const applyProgress = ({ loaded, total }) => {
        if (abort.canceled) return;
        const size = total || (uploadedFile ? uploadedFile.size : 0);
        const elapsed = (Date.now() - uploadStartRef.current) / 1000;
        uploadSentRef.current = loaded;
        setUploadProgress(size ? Math.round((loaded / size) * 100) : 0);
        setUploadSpeed(elapsed > 0 ? loaded / elapsed : 0);
        setUploadDetail({ loaded, total: size });
      };

      let id;
      if (uploadedFile) {
        // 先確認瀏覽器真的讀得到這個檔（App 內建瀏覽器、雲端相簿常在這裡就失敗）
        try {
          await ensureFileReadable(uploadedFile);
          if (abort.canceled) return;
        } catch (e) {
          clientError('file_unreadable', { name: uploadedFile.name, size: uploadedFile.size, message: e.message });
          throw e;
        }
        // 分段續傳：斷線只補缺的那幾塊，不必整份重傳
        id = await uploadFileInChunks({
          apiBase: API_BASE,
          file: uploadedFile,
          onProgress: applyProgress,
          shouldStop: () => abort.canceled,
          onEvent: (event, data) => {
            if (abort.canceled) return;
            clientLog(event, data);
            if (event === 'upload_resume') {
              setToastMsg(`偵測到上次未完成的上傳，續傳中（已傳 ${data.received}/${data.chunks} 塊）`);
              setTimeout(() => setToastMsg(''), 5000);
            } else if (event === 'upload_chunk_retry') {
              setToastMsg(`上傳中斷，正在自動續傳（第 ${data.attempt} 次重試）`);
            } else if (event === 'upload_complete') {
              setToastMsg('');
            }
          },
        });
        if (abort.canceled) return;
        setResumeHint(false);
      } else {
        // YouTube 網址沒有檔案可以切，照舊走 multipart 的 /upload/
        const form = new FormData();
        form.append('file', new File([], 'empty.wav'));
        form.append('url', url);
        const res = await axios.post(`${API_BASE}/upload/`, form);
        id = res.data.upload_id;
      }

      if (abort.canceled) return;
      setLogUploadId(id);
      clientLog('upload_ok', { upload_id: id, ms: Date.now() - uploadStartRef.current, bytes: uploadSentRef.current });
      setUploadId(id);
      setCookie('upload_id', id, 1);     // Cookie 存 1 天
      setWsStatus('connecting');
      setUploadProgress(0);
    } catch (err) {
      if (abort.canceled) return;
      setToastMsg('');
      if (err.canceled) {
        clientLog('upload_canceled', { sent: uploadSentRef.current });
        setUploadProgress(0);
        return;                        // 使用者自己按的重置，不是錯誤
      }
      clientError('upload_error', {
        code: err.code, status: err.response?.status, message: err.message,
        sent: uploadSentRef.current, name: uploadedFile?.name, size: uploadedFile?.size,
      });
      setErrorMsg(describeUploadError(err, uploadSentRef.current, uploadedFile));
      setUploadRetryable(true);
      setUploadProgress(0);
    } finally {
      if (uploadAbortRef.current === abort) {
        abort.busy = false;
        setSubmitting(false);
      }
    }
  };

  // ── WebSocket 連線 + 轉錄流程（含斷線自動重連）───────────────────
  // 電腦睡眠、手機切到背景、網路切換都會讓 WebSocket 斷掉，但後端的任務照跑。
  // 這裡負責：斷線後退避重連、喚醒／回前景時立刻重連、靠心跳判斷死連線；
  // 重連時送同一個 upload_id，後端會接回進行中的任務或直接回傳快取結果。
  useEffect(() => {
    if (!uploadId) return;
    let ws = null;
    let closedByUs = false;   // 我們自己關的（換 uploadId、重設）就不重連
    let finished = false;     // 收到 done / error 之後不再重連
    let retryTimer = null;
    let attempt = 0;
    let lastMsgAt = Date.now();
    let connectionStartedAt = Date.now();
    let lastQueuePos = null;

    const sendParams = (socket) => {
      socket.send(JSON.stringify({
        upload_id:        uploadId,
        model:            selectedModel,
        language:         selectedLang,
        task:             selectedTask,
        use_audio_optimization: useAudioOptimization,
        use_diarization:  useDiarization,
      }));
    };

    const closeCurrentSocket = () => {
      if (!ws) return;
      const socket = ws;
      ws = null;
      socket.onopen = socket.onmessage = socket.onerror = socket.onclose = null;
      if (wsRef.current === socket) wsRef.current = null;
      socket.close();
    };

    const scheduleReconnect = (why) => {
      if (closedByUs || finished) return;
      attempt += 1;
      const delay = Math.min(30000, 1000 * 2 ** Math.min(attempt, 5));   // 2s → 4s → … → 30s
      console.warn(`WS 斷線（${why}），${delay / 1000} 秒後重連（第 ${attempt} 次）`);
      clientLog('ws_reconnect_scheduled', { attempt, delay, why });
      setWsStatus('reconnecting');
      clearTimeout(retryTimer);
      retryTimer = setTimeout(connect, delay);
    };

    // 喚醒／回前景／網路恢復：不等退避計時，馬上重連
    const reconnectNow = (e) => {
      if (closedByUs || finished) return;
      const now = Date.now();
      if (ws?.readyState === WebSocket.OPEN && now - lastMsgAt <= 60000) return;
      if (ws?.readyState === WebSocket.CONNECTING && now - connectionStartedAt <= 30000) return;
      clientLog('ws_reconnect_now', { trigger: e && e.type ? e.type : 'watchdog' });
      clearTimeout(retryTimer);
      attempt = 0;
      connect();
    };

    function connect() {
      if (closedByUs || finished) return;
      clearTimeout(retryTimer);
      retryTimer = null;
      closeCurrentSocket();
      connectionStartedAt = Date.now();
      const socket = new WebSocket(buildWsUrl(API_BASE, '/ws/transcribe/'));
      ws = socket;
      wsRef.current = socket;

      ws.onopen = () => {
        if (socket !== ws || closedByUs || finished) return;
        clientLog('ws_open', { reconnect: attempt > 0 });
        attempt = 0;
        lastMsgAt = Date.now();
        setWsStatus('streaming');
        sendParams(socket);
      };

      ws.onmessage = evt => {
        if (socket !== ws || closedByUs || finished) return;
        lastMsgAt = Date.now();
        const msg = JSON.parse(evt.data);
        if (msg.ping) return;   // 後端心跳，只用來更新 lastMsgAt
        console.log('WS 收到：', msg);
        // 狀態轉換記一筆（downloading 每 0.5 秒一筆太吵，排隊只記位置變化）
        if (msg.status && msg.status !== 'downloading' && msg.status !== 'queued') {
          clientLog('ws_status', { status: msg.status, elapsed: msg.elapsed });
        }
        if (msg.status === 'queued' && msg.position !== lastQueuePos) {
          lastQueuePos = msg.position;
          clientLog('ws_queued', { position: msg.position, ahead: msg.ahead, eta: msg.eta_seconds });
        }

        // 取消是預期中的結束方式：後端送 canceled，這裡安靜地收回 idle
        if (msg.canceled) {
          finished = true;
          clientLog('ws_canceled', { upload_id: uploadId });
          backToIdle('已取消');
          return;
        }

        if (msg.error) {
          finished = true;
          clientError('ws_error_msg', { error: msg.error });
          setResultReceived(false);
          // 後端內部錯誤多半是英文的例外訊息，至少讓人知道這是伺服器的問題、不是自己做錯
          const rawErr = String(msg.error || '');
          setErrorMsg(/[\u4e00-\u9fff]/.test(rawErr) ? rawErr : `伺服器處理時發生錯誤：${rawErr}`);
          clearInterval(timerRef.current);
          setWsStatus('done');
          notifyIfHidden({
            title: '轉錄失敗',
            body: `${jobNameRef.current || '你的檔案'}：${msg.error}`,
            flash: '❌ 轉錄失敗',
          });
          return;
        }

        // 重連後接回進行中的任務：後端會附上預估總時間與已經跑了多久
        if (msg.status === 'resumed' || msg.status === 'transcribing') {
          console.log('已接回進行中的任務，已跑', msg.elapsed, '秒');
          setQueueInfo(null);
          setWsStatus('streaming');
        }

        if (msg.status === 'waiting_gpu') {
          clearInterval(timerRef.current);
          setQueueInfo(null);
          setGpuWaitMessage(msg.message || '正在等待 GPU 顯存，請稍候…');
          setWsStatus('waiting_gpu');
          return;
        }

        // 處理排隊狀態（同時只跑一個轉檔，其餘排隊）
        if (msg.status === 'queued') {
          setWsStatus('queued');
          setQueueInfo({
            position: msg.position || 0,
            ahead: msg.ahead != null ? msg.ahead : Math.max(0, (msg.position || 1) - 1),
            waiting: msg.waiting || 0,
            running: msg.running || 0,
            etaSeconds: msg.eta_seconds != null ? msg.eta_seconds : null,
          });
          return;
        }

        if (msg.status === 'queue_ready') {
          setQueueInfo(null);
          setWsStatus('streaming');
          // 伺服器空閒時 queue_ready 會緊接著送出（根本沒排到隊），
          // 那種情況不該跳通知；只有真的等過才提醒
          if (lastQueuePos !== null) {
            notifyIfHidden({
              title: '輪到你了',
              body: `${jobNameRef.current || '你的檔案'} 已開始轉錄`,
              flash: '▶️ 開始轉錄',
            });
          }
          return;
        }

        // 處理 YouTube 下載狀態
        if (msg.status === 'downloading') {
          setWsStatus('downloading');
          if (msg.download_progress != null) {
            setDownloadProgress(Math.round(msg.download_progress));
            setDownloadSpeed(msg.download_speed || 0);
            setDownloadDetail({
              downloaded: msg.download_downloaded || 0,
              total: msg.download_total || 0,
            });
          }
        }

        if (msg.status === 'download_complete') {
          console.log('YouTube 下載完成');
          setDownloadProgress(100);
          setWsStatus('streaming');
        }

        // 處理音訊優化狀態
        if (msg.status === 'optimizing') {
          setWsStatus('optimizing');
          console.log('音訊優化中...');
        }

        if (msg.status === 'optimization_complete') {
          console.log('音訊優化完成，等待轉錄開始');
          setWsStatus('streaming');  // 切換到轉錄狀態
        }

        // 處理說話者分離狀態
        if (msg.status === 'diarizing') {
          setWsStatus('diarizing');
          console.log('說話者分離中...');
        }

        if (msg.warning) {
          clientLog('ws_warning', { warning: msg.warning });
          console.warn('WS 警告：', msg.warning);
          setErrorMsg(`⚠️ ${msg.warning}`);
          setTimeout(() => setErrorMsg(''), 8000);
        }

        if (msg.estimated_time != null) {
          clientLog('eta', { eta: msg.estimated_time, elapsed: msg.elapsed || 0 });
          startCountdown(msg.estimated_time, msg.elapsed);
        }

        if (msg.done) {
          finished = true;
          clientLog('done', { segments: (msg.segments || []).length, chars: (msg.text || '').length });
          if (!(msg.text || '').trim()) {
            clientLog('empty_result', {
              segments: (msg.segments || []).length,
              model: selectedModel,
              language: selectedLang,
            });
          }
          flushClientLog();
          clearInterval(timerRef.current);
          setProgress(100);
          setRemaining(0);
          setOvertime(0);
          setFullText(msg.text || '');
          setResultReceived(true);
          setSegments(msg.segments || []);
          setWsStatus('done');
          setQueueInfo(null);
          setRestoredResult(null);    // 這是剛跑完的，不是還原的
          // 不刪 cookie：留著才能在重整後用 /status/ 或 /result/ 把結果找回來。
          // 結果同時存一份到 localStorage，後端重啟或離線也還看得到。
          saveLastResult({
            upload_id: uploadId,
            name: jobNameRef.current || '',
            model: selectedModel,
            language: selectedLang,
            created: new Date().toISOString(),
            text: msg.text || '',
            segments: msg.segments || [],
          });
          notifyIfHidden({
            title: '轉錄完成',
            body: `${jobNameRef.current || '你的檔案'}：共 ${(msg.segments || []).length} 段字幕`,
            flash: '✅ 轉錄完成',
          });
        }
      };

      ws.onerror = () => {
        // 瀏覽器接著一定會觸發 onclose，交給它統一處理重連
      };
      ws.onclose = (e) => {
        if (socket !== ws) return;
        clientLog('ws_close', { code: e.code, clean: e.wasClean, byUs: closedByUs, finished });
        if (closedByUs || finished) return;
        scheduleReconnect(`code ${e.code}`);
      };
    }

    // 看門狗：每 5 秒檢查一次。
    // 背景計時器延遲不代表斷線，只看實際心跳；建立逾時／關閉卡住也要能恢復。
    const watchdog = setInterval(() => {
      if (closedByUs || finished || retryTimer) return;
      const now = Date.now();
      if (!ws) return;
      const reason = ws.readyState === WebSocket.OPEN && now - lastMsgAt > 60000
        ? 'no_heartbeat'
        : ws.readyState === WebSocket.CONNECTING && now - connectionStartedAt > 30000
          ? 'connect_timeout'
          : ws.readyState === WebSocket.CLOSING || ws.readyState === WebSocket.CLOSED
            ? 'closed' : null;
      if (reason) {
        clientLog('ws_watchdog', { reason, silent: now - lastMsgAt });
        closeCurrentSocket();
        scheduleReconnect(reason);
      }
    }, 5000);

    const onVisible = () => { if (document.visibilityState === 'visible') reconnectNow(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', reconnectNow);
    window.addEventListener('focus', reconnectNow);
    window.addEventListener('pageshow', reconnectNow);

    connect();

    return () => {
      closedByUs = true;
      clearTimeout(retryTimer);
      clearInterval(watchdog);
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', reconnectNow);
      window.removeEventListener('focus', reconnectNow);
      window.removeEventListener('pageshow', reconnectNow);
      closeCurrentSocket();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadId]);

  // ── 螢幕喚醒鎖：手機上傳／轉錄期間避免螢幕關閉，系統就不會把連線切掉 ──
  // connecting 也算忙：上傳完成到 WebSocket 開啟之間 uploadProgress 已歸零，
  // 少了這個狀態喚醒鎖會在中間被放掉再重拿，畫面上的提示也會閃一下
  const processing = PROCESSING_STATUSES.includes(wsStatus);
  const busy = submitting || uploadProgress > 0 || processing;
  useEffect(() => {
    if (!busy) {
      setWakeLockOn(null);
      return;
    }
    if (!navigator.wakeLock) {
      // 舊版 iOS Safari（< 16.4）與部分 WebView 沒有這個 API，
      // 只能在畫面上請使用者自己保持螢幕開啟
      clientLog('wakelock', { ok: false, unsupported: true });
      setWakeLockOn(false);
      return;
    }
    let lock = null;
    let released = false;
    const acquire = async () => {
      if (released || document.visibilityState !== 'visible') return;
      try {
        lock = await navigator.wakeLock.request('screen');
        // 系統（省電模式、切到背景）可能單方面收回，畫面要如實反映
        if (typeof lock.addEventListener === 'function') {
          lock.addEventListener('release', () => { if (!released) setWakeLockOn(false); });
        }
        setWakeLockOn(true);
        clientLog('wakelock', { ok: true });
      } catch (e) {
        // 省電模式或使用者拒絕
        setWakeLockOn(false);
        clientLog('wakelock', { ok: false, message: e.message });
      }
    };
    acquire();
    // 分頁回到前景時喚醒鎖會被系統釋放，要重新申請
    document.addEventListener('visibilitychange', acquire);
    return () => {
      released = true;
      document.removeEventListener('visibilitychange', acquire);
      if (lock) lock.release().catch(() => {});
    };
  }, [busy]);

  // ── 重置所有狀態 ─────────────────────────
  const resetAll = () => {
    clientLog('reset');
    setLogUploadId(null);
    clearInterval(timerRef.current);
    deleteCookie('upload_id');
    clearLastResult();
    // 分段上傳會一直重試到成功為止，重置就是使用者說不要傳了
    uploadAbortRef.current.canceled = true;
    uploadAbortRef.current = { canceled: false };
    setSubmitting(false);
    clearPendingUpload();
    setResumeHint(false);
    setToastMsg('');
    setRestoredResult(null);
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
    setUploadProgress(0);
    setDownloadProgress(0);
    setDownloadSpeed(0);
    setDownloadDetail({ downloaded: 0, total: 0 });
    setUploadId(null);
    setWsStatus('idle');
    setQueueInfo(null);
    setProgress(0);
    setRemaining(0);
    setOvertime(0);
    setSegments([]);
    setEditedSegments([]);
    setFullText(''); setResultReceived(false);
    setErrorMsg('');
    setUploadRetryable(false);
    setUploadedFile(null);
    setFileDuration(null);
    setUrl('');
    setFileInputKey(Date.now()); // reset file input
  };

  const scrollToMainCard = () => {
    clientLog('hero_cta');
    const el = document.getElementById('main-card');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── 換個設定再轉一次 ───────────────────────
  // 以前這顆按鈕等於整份重傳：任務結束時暫存目錄被 rmtree 掉，換個模型就要
  // 再上傳一次同一支 2GB 的影片、再等十幾分鐘。現在後端會留下那份優化過的
  // 音檔 24 小時，這裡只要跟它要一個新的 upload_id 就能直接開跑。
  const handleRerun = async () => {
    if (uploadAbortRef.current.busy) return;
    const sourceId = uploadId || restoredResult?.upload_id;
    setErrorMsg('');
    setUploadRetryable(false);

    if (sourceId) {
      const abort = { canceled: false, busy: true };
      uploadAbortRef.current = abort;
      setSubmitting(true);
      try {
        clientLog('reuse_start', { source: sourceId, model: selectedModel, language: selectedLang });
        const res = await axios.post(`${API_BASE}/upload/reuse/${sourceId}`);
        if (abort.canceled) return;
        const newId = res.data.upload_id;
        clientLog('reuse_ok', { source: sourceId, upload_id: newId });

        jobNameRef.current = uploadedFile?.name || restoredResult?.name || res.data.filename || '';
        requestNotifyPermission();   // 權限只能在使用者手勢裡問

        clearInterval(timerRef.current);
        setSegments([]);
        setEditedSegments([]);
        setFullText(''); setResultReceived(false);
        setRestoredResult(null);
        setProgress(0);
        setRemaining(0);
        setOvertime(0);
        setQueueInfo(null);
        setLogUploadId(newId);
        setCookie('upload_id', newId, 1);
        setWsStatus('connecting');
        setUploadId(newId);          // WebSocket 的 effect 會用目前的模型／語言開跑
        return;
      } catch (e) {
        if (abort.canceled) return;
        const status = e.response?.status;
        clientLog('reuse_failed', { source: sourceId, status, message: e.message });
        if (!uploadedFile && !url) {
          setErrorMsg(status === 410
            ? '原始音檔已清除，請重新上傳'
            : '無法沿用上次的音檔，請重新上傳');
          return;
        }
        // 本機還有檔案就退回重傳（分段續傳會自己接續）
      } finally {
        if (uploadAbortRef.current === abort) {
          abort.busy = false;
          setSubmitting(false);
        }
      }
    }

    if (!uploadedFile && !url) {
      setErrorMsg('原始音檔已清除，請重新上傳');
      return;
    }
    handleUpload();
  };

  // ── 取消 ─────────────────────────────────
  // 和「重置」的差別：取消只把這一次的工作收掉，已選的檔案與所有設定原封不動
  // 留在畫面上——使用者按取消多半是想換個模型／語言重跑，不是想從頭選檔。
  const backToIdle = (toast) => {
    clearInterval(timerRef.current);
    deleteCookie('upload_id');
    setLogUploadId(null);
    setToastMsg('');
    setRestoredResult(null);
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }
    setUploadId(null);          // 這會讓 WebSocket 的 effect 收掉，不會再重連
    setWsStatus('idle');
    setQueueInfo(null);
    setUploadProgress(0);
    setDownloadProgress(0);
    setDownloadSpeed(0);
    setDownloadDetail({ downloaded: 0, total: 0 });
    setProgress(0);
    setRemaining(0);
    setOvertime(0);
    setSegments([]);
    setEditedSegments([]);
    setFullText(''); setResultReceived(false);
    setErrorMsg('');
    setUploadRetryable(false);
    if (toast) {
      setToastMsg(toast);
      setTimeout(() => setToastMsg(''), 3000);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('確定要取消這次轉錄嗎？已經跑掉的進度不會保留。')) return;
    clientLog('cancel', { upload_id: uploadId, status: wsStatus });
    try {
      // 一律走 HTTP：WebSocket 進入訂閱迴圈之後就不再讀客戶端訊息，
      // 送 {"action":"cancel"} 過去要等整份工作跑完才會被看到
      await axios.post(`${API_BASE}/cancel/${uploadId}`, null, { timeout: 15000 });
    } catch (e) {
      if (activeUploadIdRef.current !== uploadId) return;
      clientLog('cancel_failed', { status: e.response?.status, message: e.message });
      if (e.response?.status === 404) {
        backToIdle('任務已結束或已清除');
      } else {
        setErrorMsg('取消未成功，伺服器可能仍在處理。請確認網路後再按一次取消。');
      }
      return;
    }
    if (activeUploadIdRef.current === uploadId) backToIdle('已取消');
  };

  // ── 下載 TXT / SRT ─────────────────────────
  // 下載：一律使用編輯器目前的內容（editedSegments），
  // 未編輯時它就等於原始 segments
  const exportSegments = () => (editedSegments.length ? editedSegments : segments);

  const downloadBlob = (content, ext) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${uploadedFile?.name || restoredResult?.name || 'transcript'}.${ext}`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // 複製全文：來源與下載 TXT 完全一致（editedSegments 優先），
  // 否則使用者會發現複製到的是編輯前的舊版本
  const copyFullText = async () => {
    const text = buildTxt(exportSegments());
    clientLog('copy_text', { chars: text.length });
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        ok = true;
      }
    } catch (e) {
      ok = false;   // 權限被拒或不在安全內容中，往下走後備路徑
    }
    if (!ok) ok = legacyCopy(text);
    if (!ok) {
      clientError('copy_failed', { chars: text.length });
      setErrorMsg('複製失敗，請手動選取文字後複製');
      setTimeout(() => setErrorMsg(''), 5000);
      return;
    }
    setToastMsg('已複製');
    setTimeout(() => setToastMsg(''), 2000);
  };

  const downloadTxt = () => downloadBlob(buildTxt(exportSegments()), 'txt');
  const downloadSrt = () => downloadBlob(buildSrt(exportSegments()), 'srt');

  // 清除結果：把留在瀏覽器與 cookie 的痕跡一起清掉，回到初始畫面
  const clearResult = () => {
    clientLog('result_cleared', { upload_id: restoredResult?.upload_id || uploadId });
    resetAll();
  };

  // ── 台語 ⇄ ASR 26 雙向連動 ─────────────────────
  // 只有 Breeze ASR 26 聽得懂台語，兩者必須成對出現，
  // 否則會送出模型聽不懂的語言（後端會 fallback 成中文，結果變差）
  const handleSelectModel = (model) => {
    setSelectedModel(model);
    if (model === TAIGI_MODEL) {
      setSelectedLang(TAIGI_LANG);           // 選 ASR 26 → 語言自動切台語
    } else if (selectedLang === TAIGI_LANG) {
      setSelectedLang('zh');                 // 離開 ASR 26 → 台語自動回中文
    } else if (selectedLang === AUTO_LANG && !AUTO_LANG_MODELS.includes(model)) {
      setSelectedLang('zh');                 // Breeze 系列不支援自動偵測，收回中文
    }
  };

  const handleSelectLang = (lang) => {
    setSelectedLang(lang);
    if (lang === TAIGI_LANG) {
      setSelectedModel(TAIGI_MODEL);         // 選台語 → 模型自動切 ASR 26
    } else if (selectedModel === TAIGI_MODEL) {
      setSelectedModel('breeze');            // 離開台語 → 回到繁中模型
    }
  };

  // ── 畫面 ─────────────────────────────────────

  // ── 選檔後的預估時間 ───────────────────────
  // 使用者最想知道的是「這要跑多久」。後端每 10 秒回報一次各模型目前生效的
  // RTF（自適應統計的實測值），拿它乘上音長就是相當接近的估計。
  const rtfOf = (model) => {
    const info = serverStatus && serverStatus.rtf && serverStatus.rtf[model];
    const value = info && typeof info.rtf === 'number' ? info.rtf : null;
    return value && value > 0 ? value : DEFAULT_RTF;
  };

  // 「≈5× 即時」：1 秒音訊要跑幾分之一秒，倒過來就是幾倍速
  const speedLabel = (model) => `≈${Math.max(1, Math.round(1 / rtfOf(model)))}× 即時`;

  const estimateSeconds = (duration, model) =>
    duration * rtfOf(model) + (useDiarization ? duration * DIARIZATION_FACTOR : 0);

  // 預估用「約 N 分鐘」就好：算到秒反而假裝很精確
  const formatRough = (sec) => {
    if (sec < 60) return `${Math.max(1, Math.round(sec))} 秒`;
    if (sec < 3600) return `${Math.max(1, Math.round(sec / 60))} 分鐘`;
    const h = Math.floor(sec / 3600);
    const m = Math.round((sec % 3600) / 60);
    return m > 0 ? `${h} 小時 ${m} 分鐘` : `${h} 小時`;
  };

  const selectedModelLabel = MODEL_OPTIONS.find(m => m.value === selectedModel)?.label || selectedModel;

  // 目前這個模型可以選哪些語言（Whisper 系列多一個「自動偵測」）
  const langOptions = AUTO_LANG_MODELS.includes(selectedModel)
    ? [AUTO_LANG_OPTION, ...LANG_OPTIONS]
    : LANG_OPTIONS;

  // 有沒有真的辨識出東西。空白結果不是錯誤，但要有自己的畫面。
  const hasResultText = Boolean((fullText || '').trim());

  // ── 伺服器狀態標籤 ─────────────────────────
  // 同時只跑 1 個轉檔，所以「1/1」永遠只代表「現在有人在用」，
  // 舊文案卻寫成「🔴 滿載 1/1」——第一次來的人看到紅燈滿載會直接以為
  // 服務掛了或輪不到自己而關掉分頁。只有真的排到 3 個人才值得勸人晚點再來。
  const serverBadge = (() => {
    if (!serverStatus) return null;
    // 說話者分離會先讓出佇列名額才跑，這時 queue_running 是 0 但機器還在忙，
    // 取兩者的較大值才不會把「還在處理」講成「空閒」
    const running = Math.max(serverStatus.queue_running || 0, serverStatus.active_tasks || 0);
    const waiting = serverStatus.queue_waiting || 0;
    if (waiting >= 3) return `🔴 排隊 ${waiting} 人，建議稍後`;
    if (running > 0 || waiting > 0) return `🟡 處理中${waiting > 0 ? `・排隊 ${waiting} 人` : ''}`;
    return '🟢 空閒';
  })();

  // 防休眠提示：喚醒鎖拿不到時使用者至少要知道「得自己顧著螢幕」，
  // 否則螢幕一暗、系統睡眠，連線就斷了而使用者完全不知道原因。
  const wakeLockNote = wakeLockOn === null ? null : (
    <p className={`text-xs mt-2 ${wakeLockOn ? 'text-white/50' : 'text-amber-300'}`}>
      {wakeLockOn
        ? '🔒 已啟用防休眠，螢幕不會自動關閉（桌機請勿蓋上螢幕或手動睡眠）'
        : '⚠️ 這個瀏覽器無法防休眠，請手動保持螢幕開啟、不要切換 App'}
    </p>
  );

  return (
    <div className="min-h-screen bg-white font-sans text-ink">
      {toastMsg && (
        <div className="fixed top-5 right-5 z-50 bg-ink text-white font-bold px-6 py-3 rounded-pill shadow-card">
          {toastMsg}
        </div>
      )}

      {/* ── HERO ── */}
      <section className="bg-gradient-to-br from-brand-soft to-indigo-100 px-5 sm:px-6 pt-6 pb-12 sm:pb-16">
        <nav className="max-w-6xl mx-auto flex items-center justify-between gap-3 mb-10 sm:mb-14">
          <h1 className="font-black text-xl sm:text-2xl tracking-tight">🎙 Whisper-WebUI</h1>
          <div className="flex items-center gap-2 sm:gap-3">
            {serverBadge && (
              <span className="hidden sm:inline text-xs font-bold bg-white/70 text-ink px-3 py-1.5 rounded-pill whitespace-nowrap">
                {serverBadge}
              </span>
            )}
            {showDocs && (
              <div className="flex bg-white/70 rounded-pill p-1">
                <button
                  onClick={() => setActiveTab('transcribe')}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-pill text-sm font-bold transition-colors ${
                    activeTab === 'transcribe' ? 'bg-brand text-white' : 'text-ink hover:bg-white'
                  }`}
                >
                  <Send size={14} /> <span>轉錄</span>
                </button>
                <button
                  onClick={() => setActiveTab('docs')}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-pill text-sm font-bold transition-colors ${
                    activeTab === 'docs' ? 'bg-brand text-white' : 'text-ink hover:bg-white'
                  }`}
                >
                  <BookOpen size={14} /> <span>文件</span>
                </button>
              </div>
            )}
          </div>
        </nav>

        {activeTab === 'transcribe' && (
          <div className="max-w-6xl mx-auto">
            <p className="text-[11px] sm:text-xs font-bold tracking-[0.2em] text-brand mb-3">
              語音轉文字・繁中與台語最佳化
            </p>
            {/* 手機上主標字級刻意比桌機小一截：原本的 clamp 下限 2.25rem 加上
                下面兩行說明，會把上傳卡片整個推到第二屏——使用者滑進來看到的是
                一句標語，得先捲動才知道要在哪裡放檔案 */}
            <h2 className="font-black leading-[1.05] tracking-tight text-[clamp(1.75rem,6vw,4.5rem)]">
              轉錄好快！<br />真準。
            </h2>
            <p className="hidden sm:block mt-5 text-base sm:text-lg max-w-2xl text-ink/70 leading-relaxed">
              Breeze ASR 25 繁中優化、Breeze ASR 26 台語模型，支援說話者分離、
              YouTube 直接轉錄，以及 OpenAI 相容 API。
            </p>
            {serverBadge && (
              <p className="sm:hidden mt-4 text-xs font-bold bg-white/70 inline-block px-3 py-1.5 rounded-pill">
                {serverBadge}
              </p>
            )}
            {/* 手機專用：一鍵捲到上傳卡片，不必自己找 */}
            <button
              onClick={scrollToMainCard}
              className="btn-press sm:hidden mt-5 bg-brand hover:bg-brand-hover text-white font-bold text-sm px-6 py-3 rounded-pill shadow-card-sm"
            >
              開始使用 ↓
            </button>
          </div>
        )}
      </section>

      {activeTab === 'transcribe' ? (
        <>
          {/* ── 主操作卡片 ── */}
          <section id="main-card" className="max-w-6xl mx-auto px-5 sm:px-6 -mt-6 sm:-mt-8 scroll-mt-4">
            <div className="bg-white rounded-card border border-brand-border/60 p-5 sm:p-8 shadow-card">

              {/* 模型選擇：pill 分頁 */}
              <div className="mb-7">
                <h3 className="text-[11px] font-bold tracking-[0.18em] text-ink-muted mb-3" id="model-group-label">選擇模型</h3>
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby="model-group-label">
                  {MODEL_OPTIONS.map(m => (
                    <button
                      key={m.value}
                      type="button"
                      aria-pressed={selectedModel === m.value}
                      onClick={() => handleSelectModel(m.value)}
                      className={`text-sm font-bold px-4 sm:px-5 py-2 rounded-pill transition-colors ${
                        selectedModel === m.value
                          ? 'bg-brand text-white'
                          : 'bg-brand-soft text-ink hover:bg-indigo-100'
                      }`}
                    >
                      {m.label}
                      {m.tag && (
                        <span className={`ml-1.5 font-normal ${selectedModel === m.value ? 'opacity-60' : 'opacity-50'}`}>
                          {m.tag}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-ink-muted mt-2.5">
                  {MODEL_OPTIONS.find(m => m.value === selectedModel)?.hint}
                  <span className="ml-1 font-bold text-ink/70">{speedLabel(selectedModel)}</span>
                </p>
              </div>

              {/* 語言 / 任務 */}
              <div className="grid sm:grid-cols-2 gap-4 mb-7">
                <div>
                  <h3 className="text-[11px] font-bold tracking-[0.18em] text-ink-muted mb-3" id="lang-group-label">語言</h3>
                  <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby="lang-group-label">
                    {langOptions.map(l => (
                      <button
                        key={l.value}
                        type="button"
                        aria-pressed={selectedLang === l.value}
                        onClick={() => handleSelectLang(l.value)}
                        className={`text-sm font-bold px-4 py-2 rounded-pill transition-colors ${
                          selectedLang === l.value
                            ? 'bg-brand text-white'
                            : 'bg-brand-soft text-ink hover:bg-indigo-100'
                        }`}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="text-[11px] font-bold tracking-[0.18em] text-ink-muted mb-3" id="task-group-label">任務</h3>
                  <div className="flex flex-wrap gap-2" role="radiogroup" aria-labelledby="task-group-label">
                    {TASK_OPTIONS.map(t => (
                      <button
                        key={t.value}
                        type="button"
                        aria-pressed={selectedTask === t.value}
                        onClick={() => setSelectedTask(t.value)}
                        className={`text-sm font-bold px-4 py-2 rounded-pill transition-colors ${
                          selectedTask === t.value
                            ? 'bg-brand text-white'
                            : 'bg-brand-soft text-ink hover:bg-indigo-100'
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* App 內建瀏覽器警示 */}
              {inAppBrowser && (
                <div className="mb-4 rounded-card border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                  <p className="font-bold">📱 你正在 {inAppBrowser} 的內建瀏覽器中</p>
                  <p className="mt-1">
                    App 內建瀏覽器常常讀不到手機裡的音訊／影片檔，上傳會失敗。
                    請點右上角選單選「以瀏覽器開啟」，或複製網址貼到 Chrome／Safari 再上傳。
                  </p>
                </div>
              )}

              {/* 檔案拖放 */}
              {/* 這一區本來是純 div：只有滑鼠點得到，Tab 跳不進來、讀螢幕軟體
                  也唸不出它是可以操作的東西。補上 button 語意與鍵盤觸發。 */}
              <div
                role="button"
                tabIndex={0}
                aria-label={uploadedFile ? `已選擇 ${uploadedFile.name}，按 Enter 重新選擇檔案` : '選擇音訊或影片檔，也可以直接把檔案拖放到這裡'}
                className={`border-2 border-dashed rounded-card py-10 sm:py-14 px-4 text-center cursor-pointer transition-colors mb-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 ${
                  isDragging
                    ? 'border-brand bg-brand-soft'
                    : uploadedFile
                      ? 'border-brand bg-brand-soft'
                      : 'border-brand-border bg-paper hover:border-brand'
                }`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={openFilePicker}
                onKeyDown={e => {
                  // 空白鍵預設會捲動頁面，一定要 preventDefault
                  if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                    e.preventDefault();
                    openFilePicker();
                  }
                }}
              >
                <div className="text-3xl sm:text-4xl mb-3">
                  {isDragging ? '📥' : uploadedFile ? '✅' : '📁'}
                </div>
                <p className="font-bold text-base sm:text-lg break-all px-2">
                  {isDragging ? '鬆開滑鼠以上傳' : uploadedFile ? uploadedFile.name : '拖放音訊 / 影片檔到這裡'}
                </p>
                <p className="text-xs sm:text-sm text-ink-muted mt-1">
                  {uploadedFile ? '點擊可重新選擇檔案' : '或點擊選擇檔案・最大 10GB・WAV MP3 M4A MP4'}
                </p>
                <input
                  id="file-input"
                  key={fileInputKey}
                  type="file"
                  accept={MEDIA_ACCEPT}
                  onChange={onFileChange}
                  className="hidden"
                />
              </div>

              {/* 檔案資訊：音長、大小與這個模型的預估時間 */}
              {uploadedFile && (
                <p className="text-xs sm:text-sm text-ink-muted -mt-2 mb-4">
                  {fileDuration != null && <>音長 {formatDuration(fileDuration)}・</>}
                  大小 {formatSize(uploadedFile.size)}
                  {fileDuration != null && (
                    <>・預估約 {formatRough(estimateSeconds(fileDuration, selectedModel))}
                      （{selectedModelLabel}{useDiarization ? '＋說話者分離' : ''}）</>
                  )}
                </p>
              )}

              {/* 大支影片：整支都要上傳，但模型只吃聲音。手機網路下這個差別是
                  「十幾分鐘」和「一分鐘」，值得在按下去之前講一聲 */}
              {uploadedFile && isVideoFile(uploadedFile) && uploadedFile.size > LARGE_VIDEO_BYTES && (
                <div className="mb-4 rounded-card border border-brand-border bg-brand-soft p-4 text-sm text-ink/80">
                  <p className="font-bold">🎬 這是一支 {formatSize(uploadedFile.size)} 的影片</p>
                  <p className="mt-1">
                    影片會整支上傳（{formatSize(uploadedFile.size)}）；若只需要聲音，
                    先轉成 m4a／mp3 可省下大部分時間。
                  </p>
                </div>
              )}

              {/* 續傳提示：本機記得這個檔上次沒傳完 */}
              {resumeHint && (
                <div className="mb-4 rounded-card border border-brand-border bg-brand-soft p-4 text-sm text-ink/80">
                  <p className="font-bold">🔄 偵測到上次未完成的上傳</p>
                  <p className="mt-1">
                    按下「開始轉錄」會從中斷的地方接續，已經傳過的部分不用再傳一次。
                  </p>
                </div>
              )}

              {/* YouTube URL */}
              <div className="flex flex-col sm:flex-row gap-2 mb-7">
                <input
                  type="text"
                  placeholder="或貼上 YouTube 網址…"
                  value={url}
                  onChange={e => { setUrl(e.target.value); setUploadedFile(null); setFileDuration(null); }}
                  className="flex-1 border border-brand-border rounded-pill px-5 sm:px-6 py-3 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-colors"
                />
              </div>

              {/* 進階選項 */}
              {/* 進階選項。核取方塊本身只有四個字，沒有人猜得到勾了會發生什麼、
                  要付出多少時間代價，所以每一項都補一行說明 */}
              <div className="grid sm:grid-cols-2 gap-4 mb-7">
                <div>
                  <label className="flex items-center gap-3 bg-brand-soft hover:bg-indigo-100 rounded-pill px-5 py-3 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      checked={useAudioOptimization}
                      onChange={e => setUseAudioOptimization(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-bold">音訊優化</span>
                  </label>
                  <p className="text-xs text-ink-muted mt-1.5 px-2">
                    轉成單聲道 16 kHz 並正規化音量，只需幾秒，建議開啟。
                  </p>
                </div>
                <div>
                  <label className={`flex items-center gap-2.5 rounded-pill px-5 py-3 cursor-pointer transition-colors border ${
                    useDiarization ? 'bg-brand text-white border-brand' : 'bg-brand-soft border-transparent hover:bg-indigo-100'
                  }`}>
                    <input
                      type="checkbox"
                      checked={useDiarization}
                      onChange={e => setUseDiarization(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-bold">說話者分離</span>
                  </label>
                  <p className="text-xs text-ink-muted mt-1.5 px-2">
                    辨識並標示不同說話者（最多 6 位），額外約 1% 時間；需伺服器已設定 HF_TOKEN。
                  </p>
                </div>
              </div>

              {/* 伺服器忙碌提示：送出前就讓使用者知道要不要等 */}
              {wsStatus === 'idle' && serverQueue && (serverQueue.running > 0 || serverQueue.waiting > 0) && (
                <div className="flex items-center gap-2 bg-brand-soft border border-brand-border rounded-card px-4 py-2.5 mb-3">
                  <span className="w-2 h-2 rounded-full bg-brand animate-pulse shrink-0" />
                  <p className="text-xs text-ink/75">
                    <span className="font-bold text-brand">伺服器處理中</span>
                    {serverQueue.waiting > 0
                      ? ` — 另有 ${serverQueue.waiting} 個任務排隊，送出後會自動為你排入佇列`
                      : ' — 送出後會自動為你排入佇列，輪到時開始'}
                  </p>
                </div>
              )}

              {/* 主要行動 */}
              <div className="flex flex-col sm:flex-row gap-2.5">
                <button
                  onClick={wsStatus === 'done' ? handleRerun : handleUpload}
                  disabled={submitting || (wsStatus !== 'idle' && wsStatus !== 'done')}
                  className="btn-press flex-1 bg-brand hover:bg-brand-hover text-white font-bold text-base sm:text-lg py-4 rounded-pill shadow-card-sm flex items-center justify-center gap-2"
                >
                  <Send size={18} />
                  {submitting ? '準備／上傳中…'
                    : wsStatus === 'idle' ? '開始轉錄'
                    : wsStatus === 'done' ? '再轉錄一次'
                    : wsStatus === 'queued' ? `排隊中${queueInfo && queueInfo.ahead > 0 ? `（前面 ${queueInfo.ahead} 位）` : '…'}`
                    : '處理中…'}
                </button>
                {processing ? (
                  <button
                    onClick={handleCancel}
                    className="btn-press bg-white text-red-600 font-bold px-7 py-4 rounded-pill border border-red-300 hover:bg-red-50 flex items-center justify-center gap-2"
                  >
                    <XCircle size={16} /> 取消
                  </button>
                ) : (
                  <button
                    onClick={resetAll}
                    className="btn-press bg-white text-ink font-bold px-7 py-4 rounded-pill border border-brand-border hover:bg-brand-soft flex items-center justify-center gap-2"
                  >
                    <Trash2 size={16} /> 重置
                  </button>
                )}
              </div>
            </div>
          </section>

          {/* ── 上傳進度 ── */}
          {/* 到 100% 之後瀏覽器還在等伺服器把整包 body 收完並回應，
              大檔案可能卡好幾十秒；以前這段面板直接消失，畫面一片空白很像當掉 */}
          {uploadProgress > 0 && (
            <section className="max-w-6xl mx-auto px-5 sm:px-6 mt-4">
              <div className="bg-ink text-white rounded-card p-6 shadow-card">
                <div className="flex items-center justify-between mb-3 gap-3">
                  <span className="font-bold text-sm sm:text-base truncate">
                    {uploadProgress >= 100
                      ? '⏳ 上傳完成，等待伺服器回應…'
                      : <>正在上傳 <span className="text-brand-light">{uploadProgress}%</span></>}
                  </span>
                  <span className="text-xs sm:text-sm text-white/60 whitespace-nowrap">
                    {uploadProgress >= 100 ? '處理中' : formatSpeed(uploadSpeed)}
                  </span>
                </div>
                <div className="h-2.5 bg-white/15 rounded-pill overflow-hidden">
                  <div className="h-full bg-brand-light rounded-pill transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                </div>
                <p className="text-xs text-white/60 mt-2">
                  {formatSize(uploadDetail.loaded)} / {formatSize(uploadDetail.total)}
                </p>
                <p className="text-xs text-white/50 mt-1 sm:hidden">
                  手機上傳期間請保持螢幕開啟、不要切換 App，否則系統會中斷連線。
                </p>
                {wakeLockNote}
              </div>
            </section>
          )}

          {/* ── 排隊中 ── */}
          {wsStatus === 'queued' && queueInfo && (
            <section className="max-w-6xl mx-auto px-5 sm:px-6 mt-4">
              <div className="bg-ink text-white rounded-card p-6 shadow-card">
                <div className="flex items-center gap-4">
                  {/* 號碼牌 */}
                  <div className="shrink-0 w-16 h-16 rounded-card bg-brand flex flex-col items-center justify-center">
                    <span className="text-2xl font-black leading-none">{queueInfo.ahead}</span>
                    <span className="text-[9px] font-bold tracking-wider mt-0.5">人在前</span>
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm sm:text-base mb-1">
                      {queueInfo.ahead === 0
                        ? '⏳ 即將輪到你，準備開始…'
                        : `⏳ 排隊中 — 前面還有 ${queueInfo.ahead} 位`}
                      {queueInfo.etaSeconds > 0 && (
                        <span className="text-brand-light font-normal ml-2">
                          約需等待 {formatDuration(queueInfo.etaSeconds)}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-white/60">
                      伺服器同時只處理 1 個轉檔以確保速度與品質。
                      {queueInfo.ahead > 0 && ' 輪到你時會自動開始，請保持頁面開啟。'}
                    </p>

                    {/* 佇列視覺化：最多畫 8 格 */}
                    <div className="flex items-center gap-1 mt-3">
                      {Array.from({ length: Math.min(8, Math.max(1, queueInfo.ahead + 1)) }).map((_, i) => {
                        const isMe = i === Math.min(7, queueInfo.ahead);
                        return (
                          <div
                            key={i}
                            className={`h-2 flex-1 rounded-pill transition-colors ${
                              isMe ? 'bg-brand' : i === 0 ? 'bg-brand-light/70' : 'bg-white/15'
                            }`}
                            title={isMe ? '你' : i === 0 ? '處理中' : '等待中'}
                          />
                        );
                      })}
                      {queueInfo.ahead >= 8 && (
                        <span className="text-[10px] text-white/50 ml-1">+{queueInfo.ahead - 7}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* ── 處理狀態 ── */}
          {(wsStatus === 'connecting' || wsStatus === 'downloading' || wsStatus === 'optimizing' || wsStatus === 'waiting_gpu' || wsStatus === 'streaming' || wsStatus === 'diarizing' || wsStatus === 'reconnecting') && (
            <section className="max-w-6xl mx-auto px-5 sm:px-6 mt-4">
              <div className="bg-ink text-white rounded-card p-6 shadow-card">
                {wsStatus === 'connecting' ? (
                  /* 上傳完成到 WebSocket 開啟之間本來完全沒有畫面，
                     使用者只看到按鈕變成「處理中…」卻沒有任何進度 */
                  <div className="flex items-center gap-3">
                    <div className="animate-spin h-5 w-5 border-2 border-brand-light border-t-transparent rounded-full shrink-0" />
                    <div>
                      <p className="font-bold text-sm sm:text-base">🔗 上傳完成，正在連線伺服器…</p>
                      <p className="text-xs text-white/60">建立即時連線後就會開始排隊或轉錄</p>
                    </div>
                  </div>
                ) : wsStatus === 'downloading' ? (
                  <>
                    <div className="flex items-center justify-between mb-3 gap-3">
                      <span className="font-bold text-sm sm:text-base">
                        📥 下載 YouTube 音訊 <span className="text-brand-light">{downloadProgress}%</span>
                      </span>
                      <span className="text-xs sm:text-sm text-white/60 whitespace-nowrap">
                        {downloadDetail.total > 0 ? formatSpeed(downloadSpeed) : '連接中…'}
                      </span>
                    </div>
                    <div className="h-2.5 bg-white/15 rounded-pill overflow-hidden">
                      <div className="h-full bg-brand-light rounded-pill transition-all duration-300" style={{ width: `${downloadProgress}%` }} />
                    </div>
                    {downloadDetail.total > 0 && (
                      <p className="text-xs text-white/60 mt-2">
                        {formatSize(downloadDetail.downloaded)} / {formatSize(downloadDetail.total)}
                      </p>
                    )}
                  </>
                ) : wsStatus === 'optimizing' ? (
                  <div className="flex items-center gap-3">
                    <div className="animate-spin h-5 w-5 border-2 border-brand-light border-t-transparent rounded-full shrink-0" />
                    <div>
                      <p className="font-bold text-sm sm:text-base">🎵 正在優化音訊…</p>
                      <p className="text-xs text-white/60">預處理音檔以提升轉錄品質</p>
                    </div>
                  </div>
                ) : wsStatus === 'waiting_gpu' ? (
                  <div className="flex items-center gap-3" role="status" aria-live="polite">
                    <div className="animate-spin h-5 w-5 border-2 border-amber-300 border-t-transparent rounded-full shrink-0" />
                    <div>
                      <p className="font-bold text-sm sm:text-base">{gpuWaitMessage || '正在等待 GPU 顯存，請稍候…'}</p>
                      <p className="text-xs text-white/60">音檔已準備完成，顯存足夠後會自動開始轉錄，不需要重新上傳。</p>
                    </div>
                  </div>
                ) : wsStatus === 'diarizing' ? (
                  <div className="flex items-center gap-3">
                    <div className="animate-spin h-5 w-5 border-2 border-brand-light border-t-transparent rounded-full shrink-0" />
                    <div>
                      <p className="font-bold text-sm sm:text-base">🗣 正在進行說話者分離…</p>
                      <p className="text-xs text-white/60">辨識不同說話者，請稍候</p>
                    </div>
                  </div>
                ) : wsStatus === 'reconnecting' ? (
                  <div className="flex items-center gap-3">
                    <div className="animate-spin h-5 w-5 border-2 border-amber-300 border-t-transparent rounded-full shrink-0" />
                    <div>
                      <p className="font-bold text-sm sm:text-base">🔌 連線中斷，正在重新連線…</p>
                      <p className="text-xs text-white/60">伺服器會繼續處理你的檔案，不會中斷；連上後自動接回進度與結果</p>
                    </div>
                  </div>
                ) : overtime > 0 ? (
                  /* ETA 估少了。停在 99% / 00:00 不動看起來就是當掉，
                     改成誠實地講「還在跑，已經超過多久」＋不定長度動畫 */
                  <>
                    <div className="flex items-center justify-between mb-3 gap-3">
                      <span className="font-bold text-sm sm:text-base">轉錄中…</span>
                      <span className="text-xs sm:text-sm text-white/60 whitespace-nowrap">
                        已超過 {formatDuration(overtime)}
                      </span>
                    </div>
                    <div className="h-2.5 bg-white/15 rounded-pill overflow-hidden">
                      <div className="h-full w-1/3 bg-brand-light rounded-pill animate-indeterminate" />
                    </div>
                    <p className="text-xs text-white/60 mt-2">
                      比預估久一點，仍在處理中（已超過 {formatDuration(overtime)}）。
                      伺服器沒有停下來，不需要重新整理。
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-3 gap-3">
                      <span className="font-bold text-sm sm:text-base">
                        轉錄中… <span className="text-brand-light">{progress}%</span>
                      </span>
                      <span className="text-xs sm:text-sm text-white/60 whitespace-nowrap">
                        預估剩餘 {formatDuration(remaining)}
                      </span>
                    </div>
                    <div className="h-2.5 bg-white/15 rounded-pill overflow-hidden">
                      <div className="h-full bg-brand-light rounded-pill transition-all duration-500" style={{ width: `${progress}%` }} />
                    </div>
                  </>
                )}
                {wakeLockNote}
              </div>
            </section>
          )}

          {/* ── 錯誤 ── */}
          {errorMsg && (
            <section className="max-w-6xl mx-auto px-5 sm:px-6 mt-4">
              <div className="bg-red-50 border border-red-200 rounded-card p-5">
                <p className="font-bold text-red-600 text-sm">⚠️ {errorMsg}</p>
                {/* 上傳失敗多半是網路抖了一下，分段續傳會從缺的那一塊接下去，
                    不需要重選檔案、也不會從頭再傳一次 */}
                {uploadRetryable && (uploadedFile || url) && (
                  <button
                    onClick={handleUpload}
                    disabled={submitting}
                    className="btn-press mt-3 bg-brand hover:bg-brand-hover text-white font-bold text-sm px-6 py-2.5 rounded-pill flex items-center gap-2"
                  >
                    <RefreshCw size={15} /> 重試（會從中斷處接續）
                  </button>
                )}
                <p className="text-xs text-red-400 mt-2">
                  回報問題時請附上診斷碼：<code className="font-mono font-bold">{getSessionId()}</code>
                </p>
              </div>
            </section>
          )}

          {/* ── 結果 ── */}
          {/* done 之後一律顯示這一區。以前條件是 `done && fullText`，辨識出空字串
              （靜音檔、語言選錯）時整個畫面什麼都不會出現，使用者只看到按鈕從
              「處理中…」變回「再轉錄一次」，完全不知道發生了什麼事 */}
          {wsStatus === 'done' && resultReceived && !hasResultText && (
            <section className="max-w-6xl mx-auto px-5 sm:px-6 mt-4">
              <div className="bg-amber-50 border border-amber-200 rounded-card p-6">
                <p className="font-black text-lg text-amber-900">📭 沒有辨識到語音內容</p>
                <p className="text-sm text-amber-900/80 mt-1">
                  轉錄已經跑完，但這個檔案沒有產生任何文字。常見原因：
                </p>
                <ul className="text-sm text-amber-900/80 mt-3 space-y-1 list-disc pl-5">
                  <li>檔案本身沒有聲音，或音量過低（例如錄影時麥克風沒開）</li>
                  <li>語言選錯了——內容是英文卻選了中文時，模型可能什麼都吐不出來</li>
                  <li>模型不適合這段音訊（台語請選 Breeze ASR 26，多語內容建議 large-v3）</li>
                </ul>
                <p className="text-sm font-bold text-amber-900 mt-3">
                  建議換個語言或模型再轉一次。
                </p>
                <div className="flex flex-wrap gap-2.5 mt-4">
                  <button
                    onClick={handleRerun}
                    disabled={submitting}
                    className="btn-press bg-white text-brand font-bold text-sm px-6 py-3 rounded-pill border border-brand hover:bg-brand-soft flex items-center gap-2"
                  >
                    <RefreshCw size={16} /> 換個設定再轉一次
                  </button>
                  <button
                    onClick={clearResult}
                    className="btn-press bg-white text-ink font-bold text-sm px-6 py-3 rounded-pill border border-brand-border hover:bg-brand-soft flex items-center gap-2"
                  >
                    <Trash2 size={16} /> 清除結果
                  </button>
                </div>
                <p className="text-xs text-amber-900/70 mt-2">
                  音檔會保留 24 小時，上面改好語言或模型再按即可，不用重新上傳。
                </p>
              </div>
            </section>
          )}

          {wsStatus === 'done' && hasResultText && (
            <section className="max-w-6xl mx-auto px-5 sm:px-6 mt-4 space-y-4">
              <div className="animate-bounce-in bg-brand rounded-card p-5 text-center shadow-card text-white">
                <p className="font-black text-lg">
                  {restoredResult ? '📄 先前的轉錄結果' : '🎉 轉錄完成！'}
                </p>
                <p className="text-sm text-white/75 mt-0.5">
                  {restoredResult
                    ? `${restoredResult.name || '上一份檔案'}${restoredResult.created ? `・完成於 ${formatWhen(restoredResult.created)}` : ''}`
                    : '您的音訊已成功轉換為文字'}
                </p>
              </div>

              <div className="flex flex-wrap gap-2.5">
                <button
                  onClick={downloadTxt}
                  className="btn-press bg-ink hover:bg-brand text-white font-bold text-sm px-6 py-3 rounded-pill flex items-center gap-2"
                >
                  <Download size={16} /> 下載 TXT
                </button>
                <button
                  onClick={downloadSrt}
                  className="btn-press bg-ink hover:bg-brand text-white font-bold text-sm px-6 py-3 rounded-pill flex items-center gap-2"
                >
                  <Download size={16} /> 下載 SRT
                </button>
                <button
                  onClick={copyFullText}
                  className="btn-press bg-ink hover:bg-brand text-white font-bold text-sm px-6 py-3 rounded-pill flex items-center gap-2"
                >
                  <Copy size={16} /> 複製全文
                </button>
                <button
                  onClick={handleRerun}
                  disabled={submitting}
                  className="btn-press bg-white text-brand font-bold text-sm px-6 py-3 rounded-pill border border-brand hover:bg-brand-soft flex items-center gap-2"
                >
                  <RefreshCw size={16} /> 換個設定再轉一次
                </button>
                <button
                  onClick={clearResult}
                  className="btn-press bg-white text-ink font-bold text-sm px-6 py-3 rounded-pill border border-brand-border hover:bg-brand-soft flex items-center gap-2"
                >
                  <Trash2 size={16} /> 清除結果
                </button>
              </div>
              <p className="text-xs text-ink-muted -mt-1">
                「換個設定再轉一次」會沿用剛才上傳的音檔（保留 24 小時），
                在上面改好模型或語言再按即可，不用重新上傳。
              </p>

              {segments.length > 0 ? (
                <ErrorBoundary label="字幕編輯器">
                  <SubtitleEditor
                    file={uploadedFile}
                    url={url}
                    segments={segments}
                    onChange={setEditedSegments}
                  />
                </ErrorBoundary>
              ) : (
                /* 沒有分段資訊（例如只回純文字）才退回舊的唯讀卡片，
                   否則會和編輯器顯示同一份字幕造成重複 */
                <TranscriptCard
                  fileResult={{
                    file_name: uploadedFile?.name || url || restoredResult?.name || '',
                    text: (editedSegments.length ? editedSegments : segments)
                      .map(s => s.text).join(''),
                    segments: editedSegments.length ? editedSegments : segments,
                  }}
                />
              )}
            </section>
          )}

          {/* ── 說明 ── */}
          <section className="max-w-6xl mx-auto px-5 sm:px-6 mt-12 mb-16">
            <h3 className="text-[11px] font-bold tracking-[0.18em] text-ink-muted mb-4">關於本服務</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {MODEL_OPTIONS.map(m => (
                <div key={m.value} className="bg-paper border border-brand-border/40 rounded-card p-5">
                  <p className="font-bold text-sm mb-1">
                    {m.label}
                    {m.tag && <span className="ml-1.5 text-xs font-normal text-ink-muted">{m.tag}</span>}
                  </p>
                  <p className="text-xs text-ink-muted leading-relaxed">
                    {m.hint}
                    <span className="ml-1 font-bold text-ink/70">{speedLabel(m.value)}</span>
                  </p>
                </div>
              ))}
            </div>
            <p className="text-xs text-ink-muted mt-6 leading-relaxed">
              支援上傳音訊／影片檔或 YouTube 連結，透過 WebSocket 即時回報進度。
              可啟用說話者分離，自動辨識不同說話者並以顏色標籤區分。<br />
              架站人：張志豪
            </p>
          </section>
        </>
      ) : (
        <div className="px-5 sm:px-6 py-8">
          <DocsPage />
        </div>
      )}
    </div>
  );
}
